import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { metricsApi } from '@/services/metrics';

import styles from './DesempenhoPanel.module.css';

function fmt(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

function secs(s: number): string {
  const m = Math.floor(s / 60);
  const r = Math.round(s % 60);
  return m ? `${m}m${r.toString().padStart(2, '0')}s` : `${r}s`;
}

/**
 * Painel "Desempenho" — métricas reais da matéria (GA4 + Search Console),
 * puxadas pelo backend (B-11). Mostra "sem dados ainda" enquanto não há coleta
 * (site sem credencial configurada ou antes do 1º sync).
 */
export function DesempenhoPanel({ wpPostId, site }: { wpPostId: string | number; site?: string }) {
  const { t } = useTranslation([
    'common',
    'nav',
    'board',
    'articles',
    'analytics',
    'feeddrive',
    'config',
    'strategist',
  ]);
  void t;
  const { data, isLoading, isError } = useQuery({
    queryKey: ['metrics', 'article', wpPostId, site ?? ''],
    queryFn: () => metricsApi.article(wpPostId, site ?? ''),
    enabled: Boolean(site),
    staleTime: 5 * 60_000,
    retry: false,
  });

  return (
    <aside className={styles.card}>
      <header className={styles.head}>
        <span className={styles.title}>Desempenho</span>
        <span className={styles.window}>28 dias</span>
      </header>

      {isLoading && <p className={styles.muted}>Carregando…</p>}

      {(isError || (!isLoading && !data)) && (
        <p className={styles.empty}>
          Sem dados ainda. Conecte o Google Analytics / Search Console deste site (ou aguarde o
          próximo sync).
        </p>
      )}

      {data && (
        <>
          <div className={styles.section}>
            <span className={styles.sectionLabel}>Tráfego (GA4)</span>
            <div className={styles.grid}>
              <Metric big v={fmt(data.pageviews)} label="pageviews" />
              <Metric v={fmt(data.sessions)} label="sessões" />
              <Metric v={`${Math.round(data.engagement_rate * 100)}%`} label="engajamento" />
              <Metric v={secs(data.avg_engagement_time)} label="tempo médio" />
            </div>
          </div>

          <div className={styles.section}>
            <span className={styles.sectionLabel}>Busca (Search Console)</span>
            <div className={styles.grid}>
              <Metric v={fmt(data.gsc_clicks)} label="cliques" />
              <Metric v={fmt(data.gsc_impressions)} label="impressões" />
              <Metric v={`${(data.gsc_ctr * 100).toFixed(1)}%`} label="CTR" />
              <Metric
                v={data.gsc_position != null ? data.gsc_position.toFixed(1) : '—'}
                label="posição"
              />
            </div>
          </div>

          <p className={styles.footer}>
            coletado em {new Date(data.collected_at).toLocaleString('pt-BR')}
          </p>
        </>
      )}
    </aside>
  );
}

function Metric({ v, label, big }: { v: string; label: string; big?: boolean }) {
  return (
    <div className={styles.metric}>
      <span className={big ? styles.valBig : styles.val}>{v}</span>
      <span className={styles.lbl}>{label}</span>
    </div>
  );
}
